import confirmModal from 'components/dialogs/confirm';
import { applySnapshot, cast, flow, getParent, getRoot, getSnapshot, Instance, types } from 'mobx-state-tree';
import persist from 'mst-persist';
import appApi from 'service/app-api';
import { CoreApi } from 'service/axios-api';
import { ApiError } from 'service/error';
import { checkEula } from 'service/eula';
import nxLog from 'service/nx-log';
import plex from 'service/plex';
import { IRootStore } from 'store/root';
import { ProductState } from 'types/enum';
import { Branch, IBranch } from './branch';
import { InstallInfo } from './install-info';
import InstallStatus from './install-status';
import { MaintenanceStore } from './maintenance';
import { DetailAPI, ProductCommon } from './product-api';
import { ProductCmsPc } from './product-cms';

const { nxlApi, dataLayer, coreappIpc, electron } = window;

// /game-user/v1/library
export const ProductLibraryOne = types.model('ProductLibraryOne', {
    productId: types.identifier,
    productName: types.string, // for game installation folder name
});

// this is for storing info in localStorage
const ProductSession = types
    .model('ProductSession', {
        selectedArch: types.optional(types.string, ''),
    })
    .views((self) => {
        const views = {
            get parent(): IProductLibrary {
                return getParent<IProductLibrary>(self);
            },
            get productId(): string {
                return views.parent.productId;
            },
        };
        return views;
    })
    .actions((self) => {
        const actions = {
            afterAttach: flow(function* () {
                const storageKey = `product-session-${self.productId}`;
                yield persist(storageKey, self, {
                    storage: localStorage,
                });

                // default value setting
                if (!self.selectedArch || !self.parent.archList.includes(self.selectedArch)) {
                    self.selectedArch = ['10300', '50220'].includes(self.productId) ? '32 bit' : '64 bit';
                }
                ///

                window.addEventListener('storage', (e) => {
                    if (e.key !== storageKey || !e.newValue) return;
                    applySnapshot(self, JSON.parse(e.newValue));
                });
            }),
        };
        return actions;
    });

export interface IProductLibrary extends Instance<typeof ProductLibrary> {}

export const ProductLibrary = types
    .compose(
        ProductLibraryOne,
        ProductCommon,
        types.model({
            cms: types.safeReference(ProductCmsPc),
            detail: types.maybe(DetailAPI),
            maintenance: types.optional(MaintenanceStore, {}),
            installInfo: types.optional(InstallInfo, {}),
            branches: types.array(Branch),
            installStatus: types.maybe(InstallStatus),
            archList: types.optional(types.array(types.string), ['32 bit', '64 bit']),
            state: types.optional(types.string, 'Loading'),
            streamingIngame: types.optional(types.boolean, false),
            hasCharacters: types.optional(types.boolean, false), // game character existance in order to disable SDS (for MS only now)
            session: types.optional(ProductSession, {}),
        })
    )
    .named('ProductLibrary')
    .views((self) => {
        const views = {
            get currentBranch(): IBranch | undefined {
                return self.branches.find((e) => e.branchName.toLowerCase() === self.installInfo.currentBranch.toLowerCase());
            },
            get root(): IRootStore {
                return getRoot<IRootStore>(self);
            },
            get executablePath(): string {
                // only for MLB FE-2381
                // if (self.productId === '50202' && self.session.selectedArch === 'DX12') {
                //     return 'XVersion\\Sky_dx12.exe';
                // }
                if (
                    electron.arch === '64-bit' &&
                    self.session.selectedArch === '64 bit' &&
                    self.detail?.supportedSystemType.bit64 &&
                    self.detail?.executablePathBit64
                )
                    return self.detail.executablePathBit64;
                return views.currentBranch?.executablePath || self.detail?.executablePath || '';
            },
            get parameters(): string[] {
                const value = views.currentBranch?.parameter || self.detail?.parameter;
                if (value) return getSnapshot(value) || [];
                return [];
            },
            get status(): string {
                if (self.state !== 'Done') return self.state;
                if (!views.root.regionConfig.isTW && views.root.regionConfig.isKd(self.productId) && !views.root.appStore.IsKdAvailable)
                    return 'NotAvailable';
                // if (!self.access?.isDeveloper && self.access?.ipBlockes) return 'Notavailable';
                const state = self.installInfo.currentState;
                if (!state || state & ProductState.NOT_INSTALLED) {
                    if (self.maintenance.mode === 'predownload') return 'Predown';
                    return 'Down';
                }
                if (state & ProductState.UPDATE_REQUIRED) return 'Update';
                if (state & ProductState.INGAME) return 'Playing';
                if (state & ProductState.INSTALLED) {
                    if (self.maintenance.mode && !self.access?.isDeveloper) return 'Maint';
                    return 'Play';
                }
                // if (state & (ProductState.UPDATING | ProductState.DELETING | ProductState.PAUSED | ProductState.QUEUED)) return 'Install';
                return 'Install';
            },
            get isSdsPlayable(): boolean {
                return (
                    !!(self.installInfo.currentState & ProductState.INSTALLING) &&
                    (self.installStatus?.streaming === 'executable' || self.installStatus?.streaming === 'executed')
                );
            },
            get displayName(): string {
                return self.cms?.name || self.productName;
            },
            get streamingSupported(): boolean {
                return !!self.cms?.sDSEnable && !self.hasCharacters;
            },
            get trayNoticeAvailable(): boolean {
                return !!(self.installInfo.currentState & (ProductState.INSTALLED | ProductState.UPDATE_REQUIRED | ProductState.INGAME));
            },
            get isKd() {
                return views.root.regionConfig.isKd(self.productId);
            },
        };
        return views;
    })
    .actions((self) => ({
        afterAttach() {
            // only for MLB FE-2381
            // if (self.productId === '50202') {
            //     self.archList = cast(['64 bit', 'DX12', '32 bit']);
            // }
        },
        fetchDetail: flow(function* (reload = false) {
            const { data } = yield CoreApi.get(`/game-build/v1/configuration/games/${self.productId}`);
            self.detail = cast(data);
        }),
        _fetchPublicBranch: flow(function* () {
            if (!self.root.regionConfig.isTW && self.isKd && self.root.appStore.IsKdAvailable) {
                const { data: branch } = yield CoreApi.get(`/game-build/v1/branch/games/${self.productId}/public_etc`);
                self.branches = cast([branch]);
            } else {
                const { data: branch }: { data: IBranch } = yield CoreApi.get(`/game-build/v1/branch/games/${self.productId}/public`);
                branch.branchName = 'public';
                self.branches = cast([branch]);
            }
        }),
        // this branchs/all api requires access.developer permission
        _fetchBranches: flow(function* () {
            const { data: branches } = yield CoreApi.get(`/game-build/v1/branch/games/${self.productId}`);
            self.branches = cast(
                branches.map((x: any) => {
                    if (x.branchName === 'Public') x.branchName = 'public';
                    return x;
                })
            );
        }),
        fetchGameCharacters: flow(function* () {
            if (self.productId === '10100' && self.cms?.sDSEnable) {
                const {
                    data: { hasCharacters },
                }: { data: { hasCharacters: boolean } } = yield CoreApi.get('/web-game/v1/games/maplestory').catch(() => ({
                    data: {
                        hasCharacters: false,
                    },
                }));
                self.hasCharacters = hasCharacters;
            }
        }),
        setState(state: string) {
            self.state = state;
        },
        async setBranch(branchName: string) {
            if (branchName === 'Public') branchName = 'public';
            const branch = self.branches.find((x) => x.branchName === branchName);
            if (!branch && !self.isKd) {
                throw new Error(`No branch name matched with ${branchName}`);
            }

            if (self.installInfo.currentState & ProductState.NOT_INSTALLED && !self.isKd) {
                throw new Error(`Attempted to set branch for product that is not installed: ${self.productId}`);
            }
            await coreappIpc.send('setCurrentBranch', self.productId, branchName, branch?.useBranchDirectory);
            if (!self.isKd) {
                await coreappIpc.send('setProductState', self.productId, ProductState.UPDATE_REQUIRED);
            }
            nxlApi.appsLauncherEvents.pub('branchChanged', { productID: self.productId, branchName });
        },
    }))
    .actions((self) => ({
        fetchBranch: flow(function* () {
            if (self.access === undefined) yield self.fetchAccess();
            self.access?.isDeveloper ? yield self._fetchBranches() : yield self._fetchPublicBranch();
            // if previously current game branch is not in branches list, reset to public (default)
            if (
                self.installInfo.currentState &&
                self.installInfo.currentState !== 1 &&
                !self.branches.find((x) => x.branchName === self.installInfo.currentBranch)
            ) {
                if (self.isKd && !self.root.regionConfig.isTW) {
                    self.setBranch('Public_ETC');
                } else {
                    self.setBranch('public');
                }
            } else if (self.isKd && !self.access?.isDeveloper && !self.root.regionConfig.isTW) {
                self.setBranch('Public_ETC');
            }
        }),
    }))
    .actions((self) => ({
        // action button init
        init: flow(function* () {
            self.state = 'Loading';
            try {
                yield Promise.all([
                    self.fetchAccess(),
                    self.fetchDetail(),
                    self.fetchBranch(),
                    self.maintenance.fetch(),
                    self.fetchGameCharacters(),
                ]);
                self.state = 'Done';
            } catch (error) {
                if (error instanceof ApiError) {
                    const { code, status } = error;
                    const isBranchForbidden =
                        status === 403 && typeof error.url === 'string' && error.url.includes('/game-build/v1/branch/games/');
                    if (isBranchForbidden) {
                        self.state = 'NotAvailable';
                        return;
                    }

                    if (status === 403 && code === 30241 && self.productId === '50203') {
                        self.state = 'ErrorBypass';
                        return;
                    }
                }
                self.state = 'InitError';
            }
        }),
        async play() {
            const { productId, displayName } = self;
            if (self.installInfo.currentState & (ProductState.INGAME | ProductState.NOT_INSTALLED)) return;
            if (self.installInfo.currentState & (ProductState.QUEUED | ProductState.PAUSED)) {
                nxlApi.appsInstaller.pauseAll();
                nxlApi.appsInstaller.resume({ productID: productId });
                if (!self.isSdsPlayable) return;
            }
            self.setState('Loading');
            if (!(await checkEula(self.productId))) {
                self.setState('Done');
                return;
            }

            self.root.session.setLastPlayedGame(productId); // last played game

            await appApi.launchApp(self as IProductLibrary).catch((e) => {
                if (typeof e === 'string') {
                    e = new Error(e);
                }
                nxLog.send(
                    {
                        comment: 'launcher game client',
                        errorcode: e.code === 5 ? 2 : 1, // e.code === 5 -> UAC cancelation
                        stage: 905,
                        productId,
                    },
                    {
                        arena_error_code: e.code,
                        arena_error_message: e.message || JSON.stringify(e),
                    }
                );
                plex.sendStats('game_launch', e.type || 'failure', productId, { reason: JSON.stringify({ ...e, message: e.message }) });

                switch (e.code) {
                    case 2: // repair required
                        self.root.dialogs
                            .error({
                                title: 'common:notice',
                                message: 2,
                                errorData: { displayName },
                                cancelText: 'cancel',
                                confirmText: 'repair-now',
                            })
                            .then((confirm) => {
                                if (confirm) {
                                    nxlApi.appsInstaller.fixBrokenInstall({ productID: productId });
                                }
                            });
                        break;
                    case 5: // skip for UAC cancelation.
                        return;
                    case 30402: // unverified account
                        self.root.dialogs.open('EmailVerification');
                        break;
                    case 30408:
                        self.maintenance.fetch();
                        self.root.dialogs.error({ message: e.code || e.message });
                        break;
                    default:
                        console.error('launch game error', JSON.stringify(e));
                        self.root.dialogs.error({ message: e.code || e.message });
                }
                return false;
            });

            self.setState('Done');
        },
        async download() {
            if (!(await checkEula(self.productId))) return;
            dataLayer.push({
                event: 'Install and Update Flow',
                'install event': 'Click to Install Game',
                'install game name': self.displayName.toLowerCase(),
            });

            return self.root.dialogs.open('GameDownload', { productId: self.productId });
        },
        pause() {
            nxlApi.appsInstaller.pause({ productID: self.productId });
        },
        resume() {
            nxlApi.appsInstaller.resume({ productID: self.productId });
        },
        async cancel() {
            const confirmed = await confirmModal({
                msg: 'action-button:cancel-confirm',
                msgData: { display_name: self.displayName },
            });
            if (confirmed) {
                nxlApi.appsInstaller.cancel({ productID: self.productId });
            }
            return confirmed;
        },
        async uninstall(force = false) {
            if (!force) {
                const result = await confirmModal({ msg: 'confirm-uninstall' });
                if (!result) return;
            }
            coreappIpc.send('removeShortcut', self.displayName);
            nxlApi.appsInstaller.uninstallApp({ productID: self.productId });

            nxLog.send({
                comment: 'game uninstall start',
                stage: 910,
                productId: self.productId,
            });
        },
        async repairInstall(force = false) {
            if (!force) {
                const result = await confirmModal({ msg: 'confirm-repair-install' });
                if (!result) return;
            }
            await coreappIpc.send('deleteLocalManifest', self.productId);
            appApi.installApp(self as IProductLibrary);
        },
        selectArch(val: string) {
            self.session.selectedArch = val;
        },
        // location: 'desktop', 'startmenu'
        createShortcut: flow(function* (location: string) {
            if (!self.detail) yield self.fetchDetail();
            coreappIpc.send('createShortcut', self.productId, self.detail?.shortCutName, self.detail?.executablePath, location);
        }),
        setStreamingInGame(isInGame: boolean) {
            self.streamingIngame = isInGame;
        },
    }))
    .actions((self) => ({
        nextAction() {
            if (self.maintenance.mode && !self.access?.isDeveloper) return; // exception for maintenance
            switch (self.status) {
                case 'Play':
                case 'Update':
                case 'Install': // appApi.launchApp covers installing and paused state
                    self.play();
                    break;
                case 'Down':
                    self.download();
                    break;
                default:
                    return false;
            }
            return true;
        },
    }));
